import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, Users, Clock, Sparkles, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import useSubscription from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';
import FamilyTreeForm from '../components/FamilyTreeForm';
import AnalysisResults from '../components/AnalysisResults';
import AnalysisHistory from '../components/AnalysisHistory';

import SimpleSurnameForm from '../components/SimpleSurnameForm';
import ComprehensiveTribalResults from '../components/ComprehensiveTribalResults';
import { genealogyAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const AncestralLineage = () => {
  const { isAuthenticated } = useAuth();
  const { isPaidUser } = useSubscription();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisData, setAnalysisData] = useState(null);
  const [tribalAnalysisResults, setTribalAnalysisResults] = useState(null);

  const [loading, setLoading] = useState(false);
  const [userAnalyses, setUserAnalyses] = useState([]);
  const [isIndigenous, setIsIndigenous] = useState(null);
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadUserAnalyses();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Check if disclosure has been accepted before
    const disclosureAccepted = localStorage.getItem('tribalGeneratorDisclosureAccepted');
    if (!disclosureAccepted && isAuthenticated) {
      setShowDisclosureModal(true);
    }
  }, [isAuthenticated]);

  const loadUserAnalyses = async () => {
    try {
      const analyses = await genealogyAPI.getAnalyses();
      setUserAnalyses(analyses);
    } catch (error) {
      console.error('Failed to load analyses:', error);
    }
  };

  const handleIndigenousResponse = (response) => {
    setIsIndigenous(response);
    if (response) {
      setCurrentStep(1); // Go to tribal genealogy form
    } else {
      setCurrentStep(2); // Go to surname analysis
    }
  };

  const handleAnalysisSubmit = async (formData) => {
    setLoading(true);
    try {
      const analysis = await genealogyAPI.createAnalysis(formData);
      
      // Check if this is tribal genealogy analysis
      if (formData.analysis_type === 'tribal_genealogy') {
        setTribalAnalysisResults(analysis);
        setCurrentStep(3); // Move to results step
        toast({
          title: "Tribal Analysis Complete",
          description: "Your comprehensive tribal heritage analysis is ready.",
        });
      } else {
        setAnalysisData(analysis);
        setCurrentStep(3); // Move to results step
        toast({
          title: "Analysis Started",
          description: "Your genealogy analysis is being processed...",
        });
      }
      
      // Refresh analyses list
      loadUserAnalyses();
    } catch (error) {
      console.error('Analysis failed:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to submit analysis. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSurnameAnalysisComplete = (analysisData) => {
    toast({
      title: "Analysis Complete",
      description: `Successfully analyzed ${analysisData.total_surnames_analyzed} surname(s)`,
    });
  };

  const handleStepChange = (step) => {
    setCurrentStep(step);
  };

  const handleAnalysisSelect = (analysis) => {
    setAnalysisData(analysis);
    setCurrentStep(3);
  };

  const handleDisclosureAccept = () => {
    localStorage.setItem('tribalGeneratorDisclosureAccepted', 'true');
    setShowDisclosureModal(false);
  };

  const renderDisclosureModal = () => (
    <>
      {/* Modal Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="max-w-4xl mx-auto bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-between items-start">
              <CardTitle className="flex-1 text-xl font-bold text-gray-900">
                Tribal Generator Disclosure
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDisclosureAccept}
                className="h-8 w-8 p-0 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <p className="text-sm text-gray-700 leading-relaxed">
                By using the Tribal Generator, you acknowledge and understand that the information provided is based on a deep research process guided by your personal input, family lineage details, and ancestral surname data.
              </p>
              
              <p className="text-sm text-gray-700 leading-relaxed mt-4">
                Our system combines user-submitted information with curated tribal, historical, and cultural sources to generate insights into potential tribal heritage, surname origins, and ancestral affiliations. We encourage users to treat this as a guidepost for further family research, cultural exploration, and tribal connection, rather than a definitive declaration of identity.
              </p>
              
              <p className="text-sm text-gray-700 leading-relaxed mt-4">
                By proceeding, you consent to the use of your personal and genealogical information solely for the purpose of generating and storing your heritage results within the TribalRoot.Ai platform.
              </p>
            </div>
            
            <div className="flex justify-center">
              <Button
                onClick={handleDisclosureAccept}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 text-lg hover:from-blue-700 hover:to-purple-700"
              >
                I Acknowledge and Accept
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );

  const renderIndigenousScreening = () => (
    <Card className="max-w-4xl mx-auto bg-white/80 backdrop-blur-sm shadow-xl">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center">
          <Users className="w-6 h-6 mr-2 text-blue-600" />
          Heritage Assessment
        </CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-6">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Are you of Indigenous or Non-Indigenous Descent?
          </h2>
          <p className="text-gray-600 mb-6 text-lg">
            Indigenous descent includes Native American, Black, Latino, and other indigenous peoples worldwide.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              onClick={() => handleIndigenousResponse(true)}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-12 py-4 text-lg"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Indigenous Descent
            </Button>
            <Button
              onClick={() => handleIndigenousResponse(false)}
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-12 py-4 text-lg"
            >
              <Users className="mr-2 h-5 w-5" />
              Non-Indigenous Descent
            </Button>
          </div>
        </div>
        
        <div className="text-sm text-gray-500">
          <p>This helps us provide the most appropriate genealogical analysis for your heritage background.</p>
        </div>
      </CardContent>
    </Card>
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ancestral <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Lineage</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover your Indigenous heritage through comprehensive genealogy analysis and surname research.
            </p>
          </div>
          
          <Card className="max-w-2xl mx-auto bg-white/80 backdrop-blur-sm shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center">
                <Users className="w-6 h-6 mr-2 text-blue-600" />
                Sign In Required
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-gray-600">
                Please sign in to access the Ancestral Lineage analysis tools.
              </p>
              <Button 
                onClick={() => document.querySelector('[data-auth-modal]')?.click()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
              >
                Sign In to Continue
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const steps = [
    { id: 0, title: 'Heritage Assessment', icon: Users },
    { id: 1, title: 'Tribal Genealogy', icon: Clock },
    { id: 2, title: 'Surname Analysis', icon: Sparkles },
    { id: 3, title: 'Results', icon: Sparkles },
  ];



  return (
    <SubscriptionGate 
      feature="genealogy_analysis"
      title="AI Ancestry Analysis"
      description="Unlock comprehensive AI-powered ancestry analysis with historical context and cultural connections"
    >
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ancestral <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Lineage</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover your Indigenous heritage through comprehensive genealogy analysis and cultural connection.
            </p>
          </div>

        {/* Progress Steps - Only show if past initial screen */}
        {currentStep > 0 && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex items-center justify-between">
              {steps.slice(1).map((step, index) => {
                const Icon = step.icon;
                const adjustedId = step.id - 1; // Adjust for display
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                
                return (
                  <React.Fragment key={step.id}>
                    <div className={`flex flex-col items-center cursor-pointer transition-all duration-200 ${
                      isActive ? 'scale-110' : ''
                    }`} onClick={() => handleStepChange(step.id)}>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-colors duration-200 ${
                        isActive 
                          ? 'bg-blue-600 text-white' 
                          : isCompleted 
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-600'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`text-sm font-medium ${
                        isActive ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {step.title}
                      </span>
                    </div>
                    
                    {index < steps.slice(1).length - 1 && (
                      <div className={`flex-1 h-0.5 mx-4 transition-colors duration-200 ${
                        isCompleted ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="max-w-6xl mx-auto">
          {/* Initial Heritage Assessment */}
          {currentStep === 0 && renderIndigenousScreening()}

          {/* Tribal Genealogy Form - Indigenous Users */}
          {currentStep === 1 && isIndigenous && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Tribal Genealogy Analysis
                  <Button 
                    variant="outline" 
                    onClick={() => setCurrentStep(0)}
                    className="flex items-center"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FamilyTreeForm 
                  onSubmit={handleAnalysisSubmit}
                  loading={loading}
                  tribalMode={true}
                />
              </CardContent>
            </Card>
          )}

          {/* Surname Analysis - Non-Indigenous Users */}
          {currentStep === 2 && !isIndigenous && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Enhanced Family Surname Analysis
                  <Button 
                    variant="outline" 
                    onClick={() => setCurrentStep(0)}
                    className="flex items-center"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleSurnameForm onAnalysisComplete={handleSurnameAnalysisComplete} />
              </CardContent>
            </Card>
          )}

          {/* Results */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Tribal Analysis Results */}
              {tribalAnalysisResults && (
                <Card className="bg-white/80 backdrop-blur-sm shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Comprehensive Tribal Heritage Analysis
                      <Button 
                        variant="outline" 
                        onClick={() => setCurrentStep(0)}
                        className="flex items-center"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        New Analysis
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ComprehensiveTribalResults results={tribalAnalysisResults} />
                  </CardContent>
                </Card>
              )}
              
              {/* Standard Analysis Results */}
              {analysisData && !tribalAnalysisResults && (
                <Card className="bg-white/80 backdrop-blur-sm shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Analysis Results
                      <Button 
                        variant="outline" 
                        onClick={() => setCurrentStep(0)}
                        className="flex items-center"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        New Analysis
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AnalysisResults analysis={analysisData} />
                  </CardContent>
                </Card>
              )}
            </div>
          )}



          {/* Analysis History */}
          {currentStep === 0 && userAnalyses.length > 0 && (
            <div className="mt-8">
              <AnalysisHistory 
                analyses={userAnalyses} 
                onViewAnalysis={handleAnalysisSelect}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Disclosure Modal */}
      {showDisclosureModal && renderDisclosureModal()}
      </div>
    </SubscriptionGate>
  );
};

export default AncestralLineage;